<template>
  <!-- ============================================================
       【P420】运营端独立登录页（#/operation/login）
       ------------------------------------------------------------
       背景：运营端此前没有自己的登录入口 —— 必须先到 #/client（员工 H5）
             登录，再靠角色分流跳到 #/operation；退出后落到 #/operation 又弹内联层。
             本页给运营端一条**独立的、可直接分享的登录入口**：
               · 直接打开 #/operation/login 就能登录，不依赖 client
               · 退出登录后回到本页（见 util/clientAuth.js LOGIN_HOME.operation）
               · 底部保留「员工 H5 端」入口，方便运营人员切到 H5 端
       登录口径与全站一致：clientPasswordLogin + fetchProfile(prefer=operation)

       【P421】两套版式（同一份 DOM，纯 CSS 分流）：
         · 手机端（≤730px）：品牌区置顶 + **底部抽屉式表单**，全宽、圆角只在上沿、
           按钮加高到 54px、输入 16px 防 iOS 聚焦缩放、底部吃 safe-area。
         · 桌面端（>730px）：居中的浮层卡片（深色渐变背景 + 玻璃卡）。
       ============================================================ -->
  <div class="opl-root">
    <!-- 背景层：深空蓝紫渐变 + 网格 + 光斑 -->
    <div class="opl-bg" aria-hidden="true">
      <span class="opl-blob opl-b1"></span>
      <span class="opl-blob opl-b2"></span>
      <span class="opl-blob opl-b3"></span>
      <span class="opl-grid"></span>
    </div>

    <div class="opl-body">
      <!-- 品牌区 -->
      <header class="opl-head">
        <div class="opl-mark">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#fff" stroke-width="1.8"
               stroke-linecap="round" stroke-linejoin="round">
            <path d="M3 21V9.5L12 4l9 5.5V21" />
            <path d="M3 21h18" />
            <path d="M8.5 21v-5.5h7V21" />
            <path d="M8.5 12.5h7" />
          </svg>
        </div>
        <h1 class="opl-title">运营端</h1>
        <p class="opl-sub">留白空间企业服务 · 运营控制台</p>
      </header>

      <!-- 登录表单：手机端是底部抽屉，桌面端是浮层卡片 -->
      <main class="opl-card">
        <div class="opl-grab" aria-hidden="true"></div>

        <div class="opl-card-head">
          <h2>登录运营端</h2>
          <p>请使用运营人员账号登录</p>
        </div>

        <div class="opl-field">
          <label>手机号</label>
          <div class="opl-input">
            <svg class="opl-ic" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"
                 stroke-linecap="round" stroke-linejoin="round">
              <rect x="6" y="2.5" width="12" height="19" rx="3" />
              <path d="M10.5 18.5h3" />
            </svg>
            <input v-model="form.phone" type="tel" maxlength="11" inputmode="numeric"
                   placeholder="请输入手机号" autocomplete="username" @keyup.enter="submit" />
          </div>
        </div>

        <div class="opl-field">
          <label>密码</label>
          <div class="opl-input">
            <svg class="opl-ic" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"
                 stroke-linecap="round" stroke-linejoin="round">
              <rect x="4" y="10" width="16" height="11" rx="2.5" />
              <path d="M8 10V7a4 4 0 0 1 8 0v3" />
            </svg>
            <input v-model="form.password" :type="showPwd ? 'text' : 'password'"
                   placeholder="请输入密码" autocomplete="current-password" @keyup.enter="submit" />
            <span class="opl-eye" @click="showPwd = !showPwd">
              <svg v-if="!showPwd" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"
                   stroke-linecap="round" stroke-linejoin="round">
                <path d="M2.5 12S6 5.5 12 5.5 21.5 12 21.5 12 18 18.5 12 18.5 2.5 12 2.5 12z" />
                <circle cx="12" cy="12" r="3" />
              </svg>
              <svg v-else viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"
                   stroke-linecap="round" stroke-linejoin="round">
                <path d="M4 4l16 16" />
                <path d="M9.9 5.9A9.6 9.6 0 0 1 12 5.5c6 0 9.5 6.5 9.5 6.5a17 17 0 0 1-3.4 4.2" />
                <path d="M6.2 8.1A17 17 0 0 0 2.5 12S6 18.5 12 18.5a9.2 9.2 0 0 0 3.6-.7" />
              </svg>
            </span>
          </div>
        </div>

        <div class="opl-row">
          <span class="opl-check" :class="{ on: remember }" @click="remember = !remember">
            <svg v-if="remember" viewBox="0 0 24 24" fill="none" stroke="#fff" stroke-width="2.4"
                 stroke-linecap="round" stroke-linejoin="round"><path d="M4 12.5l5 5L20 6.5" /></svg>
          </span>
          <span class="opl-row-tx" @click="remember = !remember">7 天内自动登录</span>
        </div>

        <!-- 错误 / 无权限提示 -->
        <div class="opl-err" v-if="error">{{ error }}</div>
        <div class="opl-other" v-if="otherScope">
          <span>当前账号属于「{{ scopeLabel(otherScope) }}」</span>
          <a @click="goScope">前往{{ scopeLabel(otherScope) }} →</a>
        </div>

        <button class="opl-btn" :disabled="loading" @click="submit">
          <span v-if="!loading">登 录</span>
          <span v-else class="opl-spin"></span>
        </button>

        <!-- H5 端单独入口（运营人员需要切到员工 H5 时用） -->
        <div class="opl-h5">
          <span class="opl-hr"></span>
          <a @click="$router.replace('/client')">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"
                 stroke-linecap="round" stroke-linejoin="round">
              <rect x="3" y="4" width="18" height="13" rx="2" />
              <path d="M8 21h8" /><path d="M12 17v4" />
            </svg>
            进入员工 H5 端
          </a>
          <span class="opl-hr"></span>
        </div>

        <p class="opl-secure">信息加密传输 · 内部系统请勿外泄</p>
      </main>
    </div>
  </div>
</template>

<script>
import { clientPasswordLogin, fetchProfile, getAppScope, isLoggedIn } from '@/util/clientAuth.js'

const SCOPE_LABEL = { operation: '运营端', market: '市场端', finance: '财务端', crm: '员工 H5 端' }
const SCOPE_PATH = { operation: '/operation', market: '/market', finance: '/finance', crm: '/client' }

export default {
  name: 'OperationLogin',
  data() {
    return {
      form: { phone: '', password: '' },
      showPwd: false,
      remember: true,
      loading: false,
      error: '',
      otherScope: ''
    }
  },
  mounted() {
    /* 已登录但运营端画像没落地（例如从别的端直接开关这个 URL）：
       直接放行到运营端，由 #/operation 自己的权限检测决定给不给进。
       这样避免「已登录还看到登录页」的割裂感。 */
    if (isLoggedIn()) {
      this.$router.replace('/operation')
    }
  },
  methods: {
    scopeLabel(s) { return SCOPE_LABEL[s] || '其它端' },

    goScope() {
      const path = SCOPE_PATH[this.otherScope] || '/client'
      this.$router.replace(path)
    },

    submit() {
      this.error = ''
      this.otherScope = ''
      if (!/^1\d{10}$/.test(this.form.phone)) { this.error = '请输入正确的 11 位手机号'; return }
      if (!this.form.password) { this.error = '请输入密码'; return }

      this.loading = true
      clientPasswordLogin({
        phone: this.form.phone,
        password: this.form.password,
        remember: this.remember
      })
        .then(res => {
          if (!res || res.code !== 200) {
            this.loading = false
            this.error = (res && res.msg) ? res.msg : '登录失败，请检查账号密码'
            return null
          }
          /* 把 operation 排到探测链最前，省掉两次必然 403 的 market/finance 探测 */
          return fetchProfile(this.remember !== false, 'operation').then(profile => {
            const scope = (profile && profile.app_scope) || getAppScope()
            this.loading = false
            if (scope === 'operation') {
              this.$router.replace('/operation')
              return
            }
            /* 非运营账号：token 已写入，不强行清掉 —— 给一条去对应端的路，
               比让人卡在「登录成功但进不去」的空白页友好。 */
            this.otherScope = scope
            this.error = '该账号没有运营端权限'
          })
        })
        .catch(() => {
          this.loading = false
          this.error = '网络异常，请稍后重试'
        })
    }
  }
}
</script>

<style scoped>
/* ============================================================
   默认 = 手机端版式（底部抽屉）
   ============================================================ */
.opl-root {
  position: relative;
  /* 固定为视口高、自己做滚动容器：
     #app 那层是 overflow 裁剪 + 非滚动，靠 body 滚动救不了矮屏（实测 320×568 底部被裁）。 */
  height: 100vh;
  height: 100dvh;
  display: flex;
  flex-direction: column;
  overflow-x: hidden;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  background: #0f1633;
  font-family: -apple-system, BlinkMacSystemFont, 'PingFang SC', 'Microsoft YaHei', sans-serif;
  -webkit-tap-highlight-color: transparent;
}

/* ---------- 背景 ---------- */
.opl-bg {
  /* fixed：页面需要滚动时背景仍铺满视口，不会跟着滚出白边 */
  position: fixed;
  inset: 0;
  overflow: hidden;
  background: linear-gradient(158deg, #101a3d 0%, #1b2a63 42%, #2f3f9c 78%, #4a5fd9 100%);
}
.opl-blob {
  position: absolute;
  border-radius: 50%;
  filter: blur(38px);
  pointer-events: none;
}
.opl-b1 { width: 300px; height: 300px; background: #5f7bff; opacity: .34; top: -110px; right: -90px; }
.opl-b2 { width: 250px; height: 250px; background: #8f6bff; opacity: .26; bottom: 6%; left: -100px; }
.opl-b3 { width: 190px; height: 190px; background: #35d2c4; opacity: .14; top: 44%; right: -70px; }
.opl-grid {
  position: absolute;
  inset: 0;
  background-image:
    linear-gradient(rgba(255, 255, 255, .055) 1px, transparent 1px),
    linear-gradient(90deg, rgba(255, 255, 255, .055) 1px, transparent 1px);
  background-size: 46px 46px;
  -webkit-mask-image: radial-gradient(ellipse 78% 60% at 50% 30%, #000 30%, transparent 78%);
  mask-image: radial-gradient(ellipse 78% 60% at 50% 30%, #000 30%, transparent 78%);
}

.opl-body {
  position: relative;
  z-index: 2;
  display: flex;
  flex-direction: column;
  width: 100%;
  min-height: 100vh;
  min-height: 100dvh;
}

/* ---------- 品牌头（手机端：占据抽屉上方全部剩余空间并垂直居中） ---------- */
.opl-head {
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  justify-content: center;
  text-align: center;
  color: #fff;
  padding: calc(env(safe-area-inset-top, 0px) + 20px) 24px 26px;
  animation: oplDown .5s cubic-bezier(.2, .8, .25, 1) both;
}
@keyframes oplDown {
  from { opacity: 0; transform: translateY(-12px); }
  to { opacity: 1; transform: translateY(0); }
}
.opl-mark {
  width: 58px;
  height: 58px;
  margin: 0 auto 16px;
  border-radius: 17px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, .13);
  border: 1px solid rgba(255, 255, 255, .26);
  box-shadow: 0 10px 26px rgba(6, 12, 38, .4), inset 0 1px 0 rgba(255, 255, 255, .2);
  backdrop-filter: blur(8px);
}
.opl-title {
  margin: 0;
  font-size: 26px;
  font-weight: 700;
  letter-spacing: 6px;
  text-indent: 6px;
}
.opl-sub {
  margin: 9px 0 0;
  font-size: 12.5px;
  letter-spacing: 1.4px;
  color: rgba(255, 255, 255, .62);
}

/* ---------- 表单：底部抽屉 ---------- */
.opl-card {
  position: relative;
  margin-top: auto;
  width: 100%;
  padding: 14px 22px calc(env(safe-area-inset-bottom, 0px) + 22px);
  box-sizing: border-box;
  background: #fff;
  border-radius: 26px 26px 0 0;
  box-shadow: 0 -16px 50px rgba(5, 11, 34, .42);
  animation: oplUp .5s cubic-bezier(.2, .8, .25, 1) both;
}
@keyframes oplUp {
  from { opacity: 0; transform: translateY(26px); }
  to { opacity: 1; transform: translateY(0); }
}
/* 手机端顶部小横条（抽屉感） */
.opl-grab {
  width: 38px;
  height: 4px;
  margin: 0 auto 16px;
  border-radius: 3px;
  background: #e3e7f0;
}
.opl-card-head { margin-bottom: 18px; }
.opl-card-head h2 {
  margin: 0;
  font-size: 19px;
  font-weight: 700;
  color: #16204a;
  letter-spacing: .5px;
}
.opl-card-head p {
  margin: 6px 0 0;
  font-size: 12.5px;
  color: #8b93a8;
}

/* ---------- 输入 ---------- */
.opl-field { margin-bottom: 14px; }
.opl-field label {
  display: block;
  margin-bottom: 7px;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: .4px;
  color: #5b6480;
}
.opl-input {
  display: flex;
  align-items: center;
  gap: 9px;
  height: 52px;
  padding: 0 14px;
  box-sizing: border-box;
  border-radius: 14px;
  background: #f3f5fa;
  border: 1px solid transparent;
  transition: background .18s, border-color .18s, box-shadow .18s;
}
.opl-input:focus-within {
  background: #fff;
  border-color: #3d5bd9;
  box-shadow: 0 0 0 3px rgba(61, 91, 217, .13);
}
.opl-ic {
  width: 18px;
  height: 18px;
  flex: none;
  color: #9aa1b4;
}
.opl-input input {
  flex: 1;
  min-width: 0;
  height: 100%;
  border: 0;
  outline: 0;
  background: transparent;
  /* 16px：避免 iOS 聚焦时自动放大页面 */
  font-size: 16px;
  color: #16204a;
  letter-spacing: .3px;
}
.opl-input input::placeholder { color: #b3b9c8; }
.opl-eye {
  flex: none;
  width: 22px;
  height: 22px;
  color: #9aa1b4;
  cursor: pointer;
}
.opl-eye svg { width: 22px; height: 22px; display: block; }

/* ---------- 记住登录 ---------- */
.opl-row {
  display: flex;
  align-items: center;
  gap: 9px;
  margin: 2px 0 2px;
}
.opl-check {
  width: 18px;
  height: 18px;
  flex: none;
  border-radius: 5px;
  border: 1.5px solid #ccd2e0;
  background: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background .16s, border-color .16s;
}
.opl-check.on { background: #3d5bd9; border-color: #3d5bd9; }
.opl-check svg { width: 12px; height: 12px; }
.opl-row-tx { font-size: 13px; color: #6b7288; cursor: pointer; }

/* ---------- 提示 ---------- */
.opl-err {
  margin-top: 12px;
  padding: 10px 12px;
  border-radius: 10px;
  background: #fff1f0;
  border: 1px solid #ffd8d4;
  font-size: 13px;
  line-height: 1.6;
  color: #d4380d;
}
.opl-other {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-top: 8px;
  font-size: 13px;
  color: #6b7288;
}
.opl-other a {
  color: #3d5bd9;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
}
.opl-other a:hover { text-decoration: underline; }

/* ---------- 按钮 ---------- */
.opl-btn {
  width: 100%;
  height: 54px;
  margin-top: 18px;
  border: 0;
  border-radius: 15px;
  cursor: pointer;
  color: #fff;
  font-size: 17px;
  font-weight: 600;
  letter-spacing: 4px;
  text-indent: 4px;
  background: linear-gradient(135deg, #2f47b8 0%, #4a63e0 55%, #6b7ff0 100%);
  box-shadow: 0 14px 30px rgba(47, 71, 184, .38);
  transition: transform .16s, box-shadow .16s, opacity .16s;
}
.opl-btn:active { transform: translateY(1px); box-shadow: 0 8px 20px rgba(47, 71, 184, .34); }
.opl-btn[disabled] { cursor: default; opacity: .72; }
.opl-spin {
  display: inline-block;
  width: 17px;
  height: 17px;
  border: 2px solid rgba(255, 255, 255, .35);
  border-top-color: #fff;
  border-radius: 50%;
  animation: oplSpin .7s linear infinite;
}
@keyframes oplSpin { to { transform: rotate(360deg); } }

/* ---------- H5 入口 ---------- */
.opl-h5 {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 22px;
}
.opl-hr { flex: 1; height: 1px; background: #eceff5; }
.opl-h5 a {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 6px 2px;
  font-size: 13px;
  color: #6b7288;
  cursor: pointer;
  white-space: nowrap;
  transition: color .16s;
}
.opl-h5 a:hover { color: #3d5bd9; }
.opl-h5 a svg { width: 15px; height: 15px; }

.opl-secure {
  margin: 14px 0 0;
  text-align: center;
  font-size: 11.5px;
  letter-spacing: 1px;
  color: #aab1c2;
}

/* ============================================================
   桌面端（>730px）：改回居中浮层卡片，隐去抽屉横条
   ============================================================ */
@media (min-width: 731px) {
  .opl-root {
    align-items: center;
    padding: 40px 22px;
  }
  .opl-body {
    max-width: 430px;
    min-height: 0;
    margin: auto 0;   /* 垂直居中且内容超屏不裁顶 */
  }
  .opl-head {
    flex: 0 0 auto;
    display: block;
    padding: 0;
  }
  .opl-card {
    margin-top: 30px;
    padding: 30px 30px 26px;
    border-radius: 20px;
    border: 1px solid rgba(255, 255, 255, .5);
    background: rgba(255, 255, 255, .965);
    box-shadow: 0 26px 60px rgba(5, 11, 34, .46), 0 2px 6px rgba(5, 11, 34, .18);
    backdrop-filter: blur(14px);
  }
  .opl-grab { display: none; }
  .opl-input { height: 50px; border-radius: 12px; padding: 0 13px; }
  .opl-input input { font-size: 15px; }
  .opl-btn { height: 50px; border-radius: 13px; font-size: 16px; margin-top: 20px; }
  .opl-check { width: 16px; height: 16px; }
  .opl-check svg { width: 11px; height: 11px; }
  .opl-eye { width: 20px; height: 20px; }
  .opl-eye svg { width: 20px; height: 20px; }
  .opl-secure { margin-top: 18px; }
}

/* 矮屏（老机型 / 横屏）：整体收紧，尽量一屏放下，配合上面的滚动容器双保险 */
@media (max-height: 700px) {
  .opl-head { padding-top: calc(env(safe-area-inset-top, 0px) + 10px); padding-bottom: 14px; }
  .opl-mark { width: 48px; height: 48px; margin-bottom: 11px; border-radius: 15px; }
  .opl-title { font-size: 22px; letter-spacing: 5px; text-indent: 5px; }
  .opl-sub { margin-top: 6px; font-size: 12px; }
  .opl-card { padding-top: 10px; }
  .opl-grab { margin-bottom: 11px; }
  .opl-card-head { margin-bottom: 13px; }
  .opl-card-head h2 { font-size: 17px; }
  .opl-field { margin-bottom: 11px; }
  .opl-input { height: 46px; }
  .opl-btn { height: 48px; margin-top: 13px; font-size: 16px; }
  .opl-h5 { margin-top: 13px; }
  .opl-secure { margin-top: 10px; }
}

/* 超窄屏（≤360px）再压一点留白，保证一屏放得下 */
@media (max-width: 360px) {
  .opl-head { padding-top: calc(env(safe-area-inset-top, 0px) + 44px); }
  .opl-title { font-size: 23px; }
  .opl-card { padding-left: 18px; padding-right: 18px; }
  .opl-input { height: 48px; }
  .opl-btn { height: 50px; }
}
</style>



// WEBPACK FOOTER //
// src/pages/operation/login.vue